import React, { useState } from 'react';
import { 
  Globe, 
  Key, 
  Lock,
  Code,
  Send,
  FileJson,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Check,
  Copy,
  PlayCircle,
  Edit,
  Save,
  Plus,
  Trash,
  MessageSquare,
  Server,
  Layers,
  Briefcase,
  Bug,
  TrendingUp,
  Play,
  StopCircle,
  Circle,
  Activity
} from 'lucide-react';

// Structure de données de la documentation API
const API_AUTOMATA_DOCUMENTATION = {
  metadata: {
    title: "Documentation API Automata Environment",
    version: "v1.0.0",
    baseUrl: "http://localhost:8080",
    lastUpdate: "2024-12-18",
    status: "stable"
  },
  authentication: {
    type: "Bearer Token",
    description: "Authentification via JWT Bearer Token dans le header Authorization",
    example: "Authorization: Bearer <your_token>",
    obtention: "Les tokens sont obtenus via votre système d'authentification principal"
  },
  endpoints: [
    // ENVIRONMENTS
    {
      id: "env-get-all",
      category: "Environments",
      path: "/api/automata/environments",
      method: "GET",
      summary: "Récupérer tous les environnements",
      description: "Retourne la liste complète des environnements configurés dans le système",
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Liste des environnements récupérée avec succès",
          schema: [{
            id: "number",
            appType: "string",
            appName: "string",
            appVersion: "string",
            isActive: "boolean",
            isAgentDeployed: "boolean",
            agent: "object|null",
            functions: "array"
          }],
          example: [{
            id: 1,
            appType: "CORE_BANKING",
            appName: "Amplitude",
            appVersion: "1.0.0",
            isActive: true,
            isAgentDeployed: false,
            agent: null,
            functions: []
          }]
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "env-get-by-id",
      category: "Environments",
      path: "/api/automata/environments/{id}",
      method: "GET",
      summary: "Récupérer un environnement par ID",
      description: "Retourne les détails d'un environnement spécifique",
      parameters: [{
        name: "id",
        in: "path",
        required: true,
        type: "number",
        description: "ID de l'environnement"
      }],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Environnement trouvé",
          example: {
            id: 1,
            appType: "CORE_BANKING",
            appName: "Amplitude",
            appVersion: "1.0.0",
            isActive: true,
            isAgentDeployed: true,
            agent: {
              id: 1,
              agentType: "GENERO_GDC",
              ipAddress: "192.168.1.100",
              port: 8080
            }
          }
        },
        {
          status: 404,
          description: "Environnement non trouvé"
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "env-create",
      category: "Environments",
      path: "/api/automata/environments",
      method: "POST",
      summary: "Créer un nouvel environnement",
      description: "Crée un nouvel environnement dans le système",
      requestBody: {
        type: "application/json",
        schema: {
          appType: "string",
          appName: "string",
          appVersion: "string",
          isActive: "boolean",
          createdBy: "string"
        },
        example: {
          appType: "CORE_BANKING",
          appName: "Test Environment",
          appVersion: "1.0.0",
          isActive: true,
          createdBy: "admin"
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json"
      ],
      responses: [
        {
          status: 201,
          description: "Environnement créé avec succès",
          example: {
            id: 2,
            appType: "CORE_BANKING",
            appName: "Test Environment",
            appVersion: "1.0.0",
            isActive: true,
            createdDate: "2024-12-18T10:00:00Z"
          }
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "env-assign-agent",
      category: "Environments",
      path: "/api/automata/environments/{id}/assign-agent",
      method: "POST",
      summary: "Assigner un agent à un environnement",
      description: "Associe un agent actif à un environnement spécifique",
      parameters: [
        {
          name: "id",
          in: "path",
          required: true,
          type: "number",
          description: "ID de l'environnement"
        },
        {
          name: "agentId",
          in: "query",
          required: true,
          type: "number",
          description: "ID de l'agent à assigner"
        }
      ],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Agent assigné avec succès"
        },
        {
          status: 404,
          description: "Environnement ou agent non trouvé"
        },
        {
          status: 412,
          description: "Agent inactif"
        }
      ],
      security: ["Bearer Token"]
    },

    // AGENTS
    {
      id: "agent-get-all",
      category: "Agents",
      path: "/api/automata/agents",
      method: "GET",
      summary: "Récupérer tous les agents",
      description: "Retourne la liste complète des agents configurés",
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Liste des agents récupérée avec succès",
          schema: [{
            id: "number",
            agentType: "string",
            ipAddress: "string",
            port: "number",
            installationPath: "string",
            isActive: "boolean",
            environment: "object|null"
          }],
          example: [{
            id: 1,
            agentType: "GENERO_GDC",
            ipAddress: "192.168.1.100",
            port: 8080,
            installationPath: "/opt/genero",
            isActive: true,
            environment: null
          }]
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "agent-create",
      category: "Agents",
      path: "/api/automata/agents",
      method: "POST",
      summary: "Créer un nouvel agent",
      description: "Crée un nouvel agent de type spécifique",
      requestBody: {
        type: "application/json",
        schema: {
          agentType: "string",
          ipAddress: "string",
          port: "number",
          installationPath: "string",
          login: "string",
          password: "string",
          isActive: "boolean",
          createdBy: "string"
        },
        example: {
          agentType: "GENERO_GDC",
          ipAddress: "192.168.1.100",
          port: 8080,
          installationPath: "/opt/genero",
          login: "admin",
          password: "password",
          isActive: true,
          createdBy: "admin"
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json"
      ],
      responses: [
        {
          status: 201,
          description: "Agent créé avec succès"
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "agent-test-connection",
      category: "Agents",
      path: "/api/automata/agents/{id}/test-connection",
      method: "GET",
      summary: "Tester la connectivité d'un agent",
      description: "Vérifie si l'agent est accessible et répond correctement",
      parameters: [{
        name: "id",
        in: "path",
        required: true,
        type: "number",
        description: "ID de l'agent"
      }],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Test de connectivité effectué",
          example: {
            agentId: 1,
            connected: true,
            status: "CONNECTED"
          }
        }
      ],
      security: ["Bearer Token"]
    },

    // FUNCTIONS
    {
      id: "func-get-all",
      category: "Functions",
      path: "/api/automata/functions",
      method: "GET",
      summary: "Récupérer toutes les fonctions",
      description: "Retourne la liste complète des fonctions disponibles",
      parameters: [{
        name: "environmentId",
        in: "query",
        required: false,
        type: "number",
        description: "Filtrer par environnement"
      }],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Liste des fonctions récupérée avec succès",
          schema: [{
            id: "number",
            functionName: "string",
            functionDescription: "string",
            functionType: "string",
            functionDomain: "string",
            functionModule: "string",
            isActive: "boolean",
            environment: "object"
          }],
          example: [{
            id: 1,
            functionName: "Gestion des comptes",
            functionDescription: "Module de gestion des comptes bancaires",
            functionType: "BUSINESS",
            functionDomain: "FINANCE",
            functionModule: "ACCOUNTING",
            isActive: true,
            environment: {
              id: 1,
              appName: "Amplitude"
            }
          }]
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "func-create",
      category: "Functions",
      path: "/api/automata/functions",
      method: "POST",
      summary: "Créer une nouvelle fonction",
      description: "Crée une nouvelle fonction dans un environnement",
      requestBody: {
        type: "application/json",
        schema: {
          functionName: "string",
          functionDescription: "string",
          functionProgram: "string",
          functionType: "string",
          functionDomain: "string",
          functionModule: "string",
          usedByClient: "boolean",
          isSpecific: "boolean",
          isActive: "boolean",
          level: "string",
          priority: "number",
          createdBy: "string",
          environment: { id: "number" }
        },
        example: {
          functionName: "Test Function",
          functionDescription: "Function for testing",
          functionProgram: "test_program",
          functionType: "BUSINESS",
          functionDomain: "FINANCE",
          functionModule: "ACCOUNTING",
          usedByClient: true,
          isSpecific: false,
          isActive: true,
          level: "MEDIUM",
          priority: 2,
          createdBy: "admin",
          environment: {
            id: 1
          }
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json"
      ],
      responses: [
        {
          status: 201,
          description: "Fonction créée avec succès"
        }
      ],
      security: ["Bearer Token"]
    },

    // TASKS
    {
      id: "task-get-all",
      category: "Tasks",
      path: "/api/automata/tasks",
      method: "GET",
      summary: "Récupérer toutes les tâches",
      description: "Retourne la liste complète des tâches définies",
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Liste des tâches récupérée avec succès",
          schema: [{
            id: "number",
            taskName: "string",
            taskDescription: "string",
            executionStatus: "string",
            taskType: "string",
            function: "object",
            interactions: "array"
          }]
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "task-create",
      category: "Tasks",
      path: "/api/automata/tasks",
      method: "POST",
      summary: "Créer une nouvelle tâche",
      description: "Crée une nouvelle tâche d'automatisation",
      requestBody: {
        type: "application/json",
        schema: {
          taskName: "string",
          taskDescription: "string",
          taskScenario: "string",
          login: "string",
          password: "string",
          menu: "string",
          expectedResult: "string",
          isTested: "boolean",
          isActive: "boolean",
          hasInput: "boolean",
          hasOutput: "boolean",
          executionStatus: "string",
          taskStatus: "string",
          taskPriority: "string",
          taskNature: "string",
          taskType: "string",
          origin: "string",
          executionType: "string",
          createdBy: "string",
          function: { id: "number" }
        },
        example: {
          taskName: "Test Task",
          taskDescription: "Task for testing",
          taskScenario: "Log in and perform a transaction",
          login: "testuser",
          password: "password",
          menu: "transaction",
          expectedResult: "Transaction completed successfully",
          isTested: false,
          isActive: true,
          hasInput: true,
          hasOutput: true,
          executionStatus: "PENDING",
          taskStatus: "DRAFT",
          taskPriority: "MEDIUM",
          taskNature: "FUNCTIONAL",
          taskType: "ACCEPTABILITY",
          origin: "RECORD",
          executionType: "MANUAL",
          createdBy: "admin",
          function: {
            id: 1
          }
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json"
      ],
      responses: [
        {
          status: 201,
          description: "Tâche créée avec succès"
        }
      ],
      security: ["Bearer Token"]
    },

    // RECORD
    {
      id: "record-start",
      category: "Record",
      path: "/api/automata/record/start",
      method: "POST",
      summary: "Démarrer l'enregistrement",
      description: "Démarre l'enregistrement des actions utilisateur pour créer une tâche",
      requestBody: {
        type: "application/json",
        schema: {
          taskName: "string",
          login: "string",
          password: "string",
          menu: "string",
          function: { id: "number" }
        },
        example: {
          taskName: "Recording Task",
          login: "testuser",
          password: "password",
          menu: "test_menu",
          function: {
            id: 1
          }
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json",
        "User-ID: <userId>"
      ],
      responses: [
        {
          status: 200,
          description: "Enregistrement démarré",
          example: {
            success: true,
            message: "Capture started for user: admin"
          }
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "record-stop",
      category: "Record",
      path: "/api/automata/record/stop",
      method: "POST",
      summary: "Arrêter l'enregistrement",
      description: "Arrête l'enregistrement en cours",
      headers: [
        "Authorization: Bearer <token>",
        "User-ID: <userId>"
      ],
      responses: [
        {
          status: 200,
          description: "Enregistrement arrêté",
          example: {
            success: true,
            message: "Capture stopped for user: admin"
          }
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "record-save",
      category: "Record",
      path: "/api/automata/record/save",
      method: "POST",
      summary: "Sauvegarder la tâche enregistrée",
      description: "Sauvegarde la tâche avec toutes les interactions capturées",
      requestBody: {
        type: "application/json",
        schema: {
          taskName: "string",
          taskDescription: "string",
          login: "string",
          password: "string",
          menu: "string",
          expectedResult: "string",
          function: { id: "number" },
          interactions: "array"
        },
        example: {
          taskName: "Recorded Task",
          taskDescription: "Task recorded from user actions",
          login: "testuser",
          password: "password",
          menu: "test_menu",
          expectedResult: "Task completed successfully",
          function: {
            id: 1
          },
          interactions: []
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json",
        "User-ID: <userId>"
      ],
      responses: [
        {
          status: 200,
          description: "Tâche sauvegardée avec succès",
          example: {
            success: true,
            message: "Task saved for user: admin",
            taskId: 5,
            interactionsCount: 12
          }
        }
      ],
      security: ["Bearer Token"]
    },

    // EXECUTE
    {
      id: "execute-start",
      category: "Execute",
      path: "/api/automata/execute/start",
      method: "POST",
      summary: "Démarrer l'exécution d'une tâche",
      description: "Lance l'exécution automatique d'une tâche préenregistrée",
      requestBody: {
        type: "application/json",
        schema: {
          id: "number"
        },
        example: {
          id: 1
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json",
        "User-ID: <userId>"
      ],
      responses: [
        {
          status: 200,
          description: "Exécution démarrée",
          example: {
            success: true,
            message: "Execution started for user: admin"
          }
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "execute-stop",
      category: "Execute",
      path: "/api/automata/execute/stop",
      method: "POST",
      summary: "Arrêter l'exécution en cours",
      description: "Interrompt l'exécution d'une tâche en cours",
      headers: [
        "Authorization: Bearer <token>",
        "User-ID: <userId>"
      ],
      responses: [
        {
          status: 200,
          description: "Exécution arrêtée",
          example: {
            success: true,
            message: "Execution stopped for user: admin"
          }
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "execute-interactions",
      category: "Execute",
      path: "/api/automata/execute/interactions",
      method: "GET",
      summary: "Récupérer les interactions d'une tâche",
      description: "Retourne toutes les interactions associées à une tâche pour l'exécution",
      parameters: [{
        name: "taskId",
        in: "query",
        required: true,
        type: "number",
        description: "ID de la tâche"
      }],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Liste des interactions récupérée",
          schema: [{
            id: "number",
            sequence: "number",
            type: "string",
            direction: "string",
            status: "string",
            timestamp: "string",
            technicalProperties: "object"
          }]
        }
      ],
      security: ["Bearer Token"]
    },

    // ANOMALIES
    {
      id: "anomaly-get-all",
      category: "Anomalies",
      path: "/api/automata/anomalies",
      method: "GET",
      summary: "Récupérer toutes les anomalies",
      description: "Retourne la liste des anomalies avec filtres optionnels",
      parameters: [
        {
          name: "functionId",
          in: "query",
          required: false,
          type: "number",
          description: "Filtrer par fonction"
        },
        {
          name: "status",
          in: "query",
          required: false,
          type: "string",
          description: "Filtrer par statut (NEW, ASSIGNED, IN_PROGRESS, RESOLVED, CLOSED, REJECTED)"
        }
      ],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Liste des anomalies récupérée avec succès"
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "anomaly-create",
      category: "Anomalies",
      path: "/api/automata/anomalies",
      method: "POST",
      summary: "Créer une nouvelle anomalie",
      description: "Crée une nouvelle anomalie liée à une fonction",
      requestBody: {
        type: "application/json",
        schema: {
          title: "string",
          description: "string",
          type: "string",
          severity: "string",
          impact: "string",
          status: "string",
          detectedBy: "string",
          function: { id: "number" }
        },
        example: {
          title: "Test Anomaly",
          description: "Anomaly for testing",
          type: "FUNCTIONAL",
          severity: "MEDIUM",
          impact: "MINOR",
          status: "NEW",
          detectedBy: "admin",
          function: {
            id: 1
          }
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json"
      ],
      responses: [
        {
          status: 201,
          description: "Anomalie créée avec succès"
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "anomaly-assign",
      category: "Anomalies",
      path: "/api/automata/anomalies/{id}/assign",
      method: "PATCH",
      summary: "Assigner une anomalie",
      description: "Assigne une anomalie à un utilisateur pour résolution",
      parameters: [
        {
          name: "id",
          in: "path",
          required: true,
          type: "number",
          description: "ID de l'anomalie"
        },
        {
          name: "userId",
          in: "query",
          required: true,
          type: "number",
          description: "ID de l'utilisateur assigné"
        }
      ],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Anomalie assignée avec succès"
        }
      ],
      security: ["Bearer Token"]
    },

    // EVOLUTIONS
    {
      id: "evolution-get-all",
      category: "Evolutions",
      path: "/api/automata/evolutions",
      method: "GET",
      summary: "Récupérer toutes les évolutions",
      description: "Retourne la liste des évolutions avec filtres optionnels",
      parameters: [
        {
          name: "functionId",
          in: "query",
          required: false,
          type: "number",
          description: "Filtrer par fonction"
        },
        {
          name: "status",
          in: "query",
          required: false,
          type: "string",
          description: "Filtrer par statut (REQUESTED, APPROVED, ASSIGNED, IN_PROGRESS, IMPLEMENTED, TESTED, DEPLOYED, REJECTED)"
        }
      ],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Liste des évolutions récupérée avec succès"
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "evolution-create",
      category: "Evolutions",
      path: "/api/automata/evolutions",
      method: "POST",
      summary: "Créer une nouvelle évolution",
      description: "Crée une demande d'évolution pour une fonction",
      requestBody: {
        type: "application/json",
        schema: {
          title: "string",
          description: "string",
          type: "string",
          priority: "string",
          status: "string",
          requestedBy: "string",
          function: { id: "number" }
        },
        example: {
          title: "Test Evolution",
          description: "Evolution for testing",
          type: "FEATURE",
          priority: "MEDIUM",
          status: "REQUESTED",
          requestedBy: "admin",
          function: {
            id: 1
          }
        }
      },
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json"
      ],
      responses: [
        {
          status: 201,
          description: "Évolution créée avec succès"
        }
      ],
      security: ["Bearer Token"]
    },
    {
      id: "evolution-approve",
      category: "Evolutions",
      path: "/api/automata/evolutions/{id}/approve",
      method: "POST",
      summary: "Approuver une évolution",
      description: "Approuve une évolution en statut REQUESTED",
      parameters: [{
        name: "id",
        in: "path",
        required: true,
        type: "number",
        description: "ID de l'évolution"
      }],
      headers: ["Authorization: Bearer <token>"],
      responses: [
        {
          status: 200,
          description: "Évolution approuvée avec succès"
        },
        {
          status: 400,
          description: "L'évolution doit être en statut REQUESTED"
        }
      ],
      security: ["Bearer Token"]
    }
  ]
};

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Composant Badge pour les méthodes HTTP
const MethodBadge = ({ method }) => {
  const colors = {
    GET: 'bg-green-100 text-green-800',
    POST: 'bg-blue-100 text-blue-800',
    PUT: 'bg-yellow-100 text-yellow-800',
    DELETE: 'bg-red-100 text-red-800',
    PATCH: 'bg-purple-100 text-purple-800'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[method]}`}>
      {method}
    </span>
  );
};

// Composant de visualisation JSON
const JsonViewer = ({ data, title }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="relative">
      {title && (
        <div className="text-sm font-medium text-gray-700 mb-2">{title}</div>
      )}
      <div className="bg-gray-900 rounded-lg p-4 text-gray-100 font-mono text-sm overflow-x-auto">
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </div>
      <button
        onClick={copyToClipboard}
        className="absolute top-2 right-2 p-2 rounded-md hover:bg-gray-700 text-gray-400 hover:text-gray-200"
      >
        {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

// Icône de catégorie
const CategoryIcon = ({ category }) => {
  const icons = {
    Environments: <Layers className="h-5 w-5" />,
    Agents: <Server className="h-5 w-5" />,
    Functions: <Code className="h-5 w-5" />,
    Tasks: <Briefcase className="h-5 w-5" />,
    Record: <Circle className="h-5 w-5" />,
    Execute: <Play className="h-5 w-5" />,
    Anomalies: <Bug className="h-5 w-5" />,
    Evolutions: <TrendingUp className="h-5 w-5" />
  };

  return icons[category] || <Activity className="h-5 w-5" />;
};

// Composant de documentation d'endpoint
const EndpointDoc = ({ endpoint, isEditing, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [testRequest, setTestRequest] = useState(
    endpoint.requestBody ? JSON.stringify(endpoint.requestBody.example, null, 2) : ''
  );

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-4">
          <CategoryIcon category={endpoint.category} />
          <MethodBadge method={endpoint.method} />
          <div className="font-mono text-sm">{endpoint.path}</div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          {isExpanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </button>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{endpoint.summary}</h4>
              <p className="text-gray-600">{endpoint.description}</p>
            </div>

            {endpoint.parameters && endpoint.parameters.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Paramètres</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  {endpoint.parameters.map((param, index) => (
                    <div key={index} className="mb-2">
                      <span className="font-mono text-sm font-medium">{param.name}</span>
                      <span className="text-gray-600 ml-2">({param.in})</span>
                      {param.required && <span className="text-red-500 ml-2">*</span>}
                      <p className="text-sm text-gray-600 mt-1">{param.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {endpoint.requestBody && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Request Body</h4>
                <JsonViewer 
                  data={endpoint.requestBody.example} 
                  title="Example Request"
                />
              </div>
            )}

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Responses</h4>
              <div className="space-y-4">
                {endpoint.responses.map((response, index) => (
                  <div key={index}>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        response.status >= 200 && response.status < 300
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {response.status}
                      </span>
                      <span className="text-gray-600">{response.description}</span>
                    </div>
                    {response.example && (
                      <JsonViewer 
                        data={response.example} 
                        title="Example Response"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {endpoint.security && endpoint.security.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Sécurité</h4>
                <div className="flex space-x-2">
                  {endpoint.security.map((security, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                    >
                      {security}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {endpoint.headers && endpoint.headers.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Headers requis</h4>
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
                  {endpoint.headers.map((header, index) => (
                    <div key={index}>{header}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Console de Test intégrée */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-4">Console de Test</h3>
              <div className="space-y-4">
                {endpoint.requestBody && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Corps de la requête
                    </label>
                    <textarea
                      rows={6}
                      className="w-full p-3 border rounded-lg font-mono text-sm"
                      value={testRequest}
                      onChange={(e) => setTestRequest(e.target.value)}
                    />
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    className="flex items-center space-x-2"
                    onClick={() => {
                      console.log('Test request sent:', {
                        method: endpoint.method,
                        path: endpoint.path,
                        body: testRequest
                      });
                    }}
                  >
                    <PlayCircle className="h-4 w-4" />
                    <span>Tester</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant principal de documentation API
const DocumentationAPIAutomata = () => {
  const [apiData, setApiData] = useState(API_AUTOMATA_DOCUMENTATION);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', ...new Set(apiData.endpoints.map(e => e.category))];
  
  const filteredEndpoints = apiData.endpoints.filter(endpoint => 
    selectedCategory === 'all' || endpoint.category === selectedCategory
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {apiData.metadata.title}
            </h1>
            <p className="mt-1 text-gray-600">
              Version {apiData.metadata.version} - Base URL: {apiData.metadata.baseUrl}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2"
          >
            {isEditing ? (
              <>
                <Save className="h-4 w-4" />
                <span>Sauvegarder</span>
              </>
            ) : (
              <>
                <Edit className="h-4 w-4" />
                <span>Modifier</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Section Authentification */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Lock className="h-5 w-5 text-blue-500" />
            <span>Authentication</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900">Type</h4>
              <p className="text-gray-600">{apiData.authentication.type}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Description</h4>
              <p className="text-gray-600">{apiData.authentication.description}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Example</h4>
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
                {apiData.authentication.example}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtre par catégorie */}
      <div className="mb-6">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex items-center space-x-2 ${
                selectedCategory === category
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category !== 'all' && <CategoryIcon category={category} />}
              <span>{category.charAt(0).toUpperCase() + category.slice(1)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Liste des endpoints */}
      <div className="space-y-6">
        {filteredEndpoints.map((endpoint) => (
          <EndpointDoc
            key={endpoint.id}
            endpoint={endpoint}
            isEditing={isEditing}
            onEdit={(updated) => {
              const updatedEndpoints = apiData.endpoints.map(e =>
                e.id === updated.id ? updated : e
              );
              setApiData({ ...apiData, endpoints: updatedEndpoints });
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default DocumentationAPIAutomata;